import { merge } from 'lodash-es';

import type { AnnotationsTooltipConfig } from '../components/tooltip/types/annotations-tooltip-props.js';
import type { AnnotationsConfigProperties } from '../types/annotations-config-properties.js';
import type { AnnotationsTrackConfig } from '../types/annotations-track-props.js';
import type { TextOverflowOptions } from '../types/marker.js';
import { populateMissingContextTracksProps } from './populate-missing-context-tracks-props.js';

type AnnotationsSlots = {
  tracks?: unknown[];
  tooltip?: AnnotationsTooltipConfig;
  showLabels?: boolean;
  textOverflow?: TextOverflowOptions;
};

/**
 * Merges a context-supplied annotations configuration with a slot-supplied one, applying the
 * standard precedence rules shared by HistogramChart and TimeseriesChart:
 * - tracks: concatenated (context first, then slots), with context tracks populated via
 *   `populateMissingContextTracksProps` to fill in defaults expected by the renderer
 * - tooltip.template: slot-only — the context config can only set `hidden`
 * - tooltip.hidden, showLabels (default `true`), textOverflow: slot-first, then context
 */
export function mergeAnnotationsConfig<TSlots extends AnnotationsSlots>(
  contextAnnotations: AnnotationsConfigProperties | undefined,
  slotsAnnotations: TSlots | undefined,
): TSlots | undefined {
  if (!contextAnnotations && !slotsAnnotations) {
    return undefined;
  }

  const base = merge({}, contextAnnotations, slotsAnnotations);

  return {
    ...base,
    tracks: populateMissingContextTracksProps(
      contextAnnotations?.tracks ?? [],
    ).concat((slotsAnnotations?.tracks ?? []) as AnnotationsTrackConfig[]),
    showLabels:
      slotsAnnotations?.showLabels ?? contextAnnotations?.showLabels ?? true,
    textOverflow:
      slotsAnnotations?.textOverflow ?? contextAnnotations?.textOverflow,
    tooltip: {
      hidden:
        slotsAnnotations?.tooltip?.hidden ??
        contextAnnotations?.tooltip?.hidden ??
        false,
      template: slotsAnnotations?.tooltip?.template,
    },
  } as TSlots;
}
